import React, { useEffect, useState, lazy, createElement } from 'react';
import { AlertTriangleIcon, ArrowRightIcon, BookOpenIcon, CheckCircle2Icon, ClockIcon, DownloadIcon, DropletsIcon, FileCheckIcon, FlameIcon, GiftIcon, HammerIcon, MapIcon, PaintbrushIcon, PenToolIcon, SettingsIcon, ShieldCheckIcon, ShieldIcon, SunIcon, ThermometerIcon, TruckIcon, WindIcon, WrenchIcon, ZapIcon, BoxIcon } from 'lucide-react';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
const CHECKOUT_URL = 'https://motorhome-guide.impultienda.ar/checkout';
const META_PIXEL_ID = '1729598455048515';
const PRODUCT_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/4N3GVBiUt4hQCGTeSodJGG/1ac68acd-20a8-4f2a-97e1-cdaa3e77911b.png';
type GuideModule = {
  icon: BoxIcon;
  number: number;
  title: string;
  subtitle: string;
  description: string;
  items: string[];
};
type Bonus = {
  image: string;
  title: string;
  description: string;
  benefit: string;
};
type MetaPixel = ((event: string, ...parameters: unknown[]) => void) & {
  callMethod?: (...parameters: unknown[]) => void;
  loaded?: boolean;
  push?: (...parameters: unknown[]) => void;
  queue?: unknown[][];
  version?: string;
};
type MetaWindow = Window & {
  _fbq?: MetaPixel;
  fbq?: MetaPixel;
  initializedMetaPixels?: string[];
  metaPageViewSent?: boolean;
};
const GUIDE_MODULES: GuideModule[] = [{
  icon: BookOpenIcon,
  number: 1,
  title: 'Foundations',
  subtitle: 'The starting point for a project that actually moves forward',
  description: 'Understand the whole project before you buy or remove anything.',
  items: ['What a functional van conversion really requires', 'Four project types and how to choose yours', 'The Sequential Build Method, step by step', 'How to calculate total weight before you invest', 'Early mistakes worth avoiding']
}, {
  icon: WrenchIcon,
  number: 2,
  title: 'Tools & Materials',
  subtitle: 'Buy only what your build truly needs',
  description: 'A practical framework for selecting tools, materials, and suppliers.',
  items: ['Essential tools for a complete conversion', 'What you can rent or borrow', 'Core materials: wood, aluminum, sealants, and adhesives', 'How to compare suppliers and technical data sheets', 'How to avoid unnecessary or incompatible purchases']
}, {
  icon: TruckIcon,
  number: 3,
  title: 'Choosing Your Vehicle',
  subtitle: 'The decision that shapes everything else',
  description: 'Choose a reliable base that fits the way you want to travel.',
  items: ['Comparing vans, minibuses, and larger vehicles', '21 points to inspect on a used vehicle', 'How to spot corrosion and structural problems', 'Capacity, layout potential, and payload', 'Key questions to ask before you buy']
}, {
  icon: PenToolIcon,
  number: 4,
  title: 'Planning & Interior Design',
  subtitle: 'The plan that saves months of rework',
  description: 'Design your space, systems, and circulation before making the first cut.',
  items: ['How to make a scale layout without technical drawing skills', 'Functional layouts for different travel styles', 'Ergonomic dimensions that make a small space livable', 'Routing wiring and plumbing before insulation', 'How to test your design with a simple mock-up']
}, {
  icon: ShieldIcon,
  number: 5,
  title: 'Vehicle Preparation',
  subtitle: 'A base that protects everything you build next',
  description: 'Prepare the structure for a durable, safe conversion.',
  items: ['Treating corrosion and protecting surfaces', 'Repairing and sealing joints, holes, and channels', 'Protecting cavities and structural profiles', 'What to remove before building inside', 'A pre-installation inspection checklist']
}, {
  icon: ThermometerIcon,
  number: 6,
  title: 'Thermal & Sound Insulation',
  subtitle: 'Comfort through every season',
  description: 'Control temperature, reduce road noise, and manage condensation from the start.',
  items: ['How common insulation materials compare', 'How to calculate the right thickness for your use', 'Installation without thermal bridges', 'Preventing condensation and moisture', 'Soundproofing the floor, walls, and roof']
}, {
  icon: DropletsIcon,
  number: 7,
  title: 'Water System',
  subtitle: 'From tank to shower, without leaks',
  description: 'Build a clean, efficient water system that is easy to maintain.',
  items: ['Tank capacity and water autonomy', 'Choosing pumps, tubing, and fittings', 'Fresh, hot, and waste-water circuits', 'Preventing odors and blockages', 'Compact bathroom and toilet options']
}, {
  icon: FlameIcon,
  number: 8,
  title: 'Gas Installation',
  subtitle: 'Safety before speed',
  description: 'Learn the principles of a responsible and verifiable gas installation.',
  items: ['Ventilated gas storage and cylinder protection', 'Suitable pipes, fittings, and regulators', 'A step-by-step leak test', 'Safe ventilation and appliance positioning', 'When to call a certified professional']
}, {
  icon: ZapIcon,
  number: 9,
  title: 'Electrical System',
  subtitle: 'The control center of your camper',
  description: 'Understand and build a protected system sized for your real needs.',
  items: ['Calculating your daily power consumption', 'Comparing battery technologies', 'Cable gauge and protection reference tables', 'Building a circuit-by-circuit distribution board', 'Choosing and installing an inverter and charger']
}, {
  icon: SunIcon,
  number: 10,
  title: 'Solar Power',
  subtitle: 'More freedom to travel farther',
  description: 'Size and install your solar setup with clear, practical criteria.',
  items: ['How many panels your consumption requires', 'PWM vs. MPPT charge controllers', 'Roof mounting, sealing, and ventilation', 'Safe battery and controller configuration', 'Real-time battery monitoring']
}, {
  icon: WindIcon,
  number: 11,
  title: 'Heating & Ventilation',
  subtitle: 'A comfortable interior wherever you park',
  description: 'Choose the solution that fits your vehicle, climate, and travel style.',
  items: ['Gas, diesel, and alternative heating options', 'How to estimate the heat output you need', 'Cross ventilation and humidity control', 'Exhaust and air-intake safety', 'How to choose reliable equipment']
}, {
  icon: HammerIcon,
  number: 12,
  title: 'Interior Build',
  subtitle: 'When your vehicle becomes a home',
  description: 'Build furniture, a kitchen, a bed, and storage made for life on the move.',
  items: ['The requirements of furniture used in a moving vehicle', 'Kitchen cabinet construction and cut lists', 'Solutions for wet rooms and compact bathrooms', 'Fixed, fold-away, and convertible beds', 'Storage that makes use of every inch']
}, {
  icon: PaintbrushIcon,
  number: 13,
  title: 'Finishing Touches',
  subtitle: 'The details that elevate the whole build',
  description: 'Give your interior a durable, clean, personal finish.',
  items: ['Suitable flooring and expansion gaps', 'Wall finishes made for moisture and movement', 'A quiet, vibration-free ceiling', 'Low-consumption LED lighting design', 'Final details for a professional result']
}, {
  icon: FileCheckIcon,
  number: 14,
  title: 'Registration & Safety',
  subtitle: 'Prepare to travel with confidence',
  description: 'A roadmap for researching the requirements that apply where you live and travel.',
  items: ['Common documents and inspections', 'How to prepare for a vehicle inspection', 'Mistakes to prevent before you finish', 'Insurance and registration questions to resolve', 'Planning for international travel']
}, {
  icon: MapIcon,
  number: 15,
  title: 'Testing & First Trip',
  subtitle: 'Test everything before you get far from home',
  description: 'Put every system through an orderly, safe testing process.',
  items: ['Testing sequence for every system', 'How to plan a shakedown trip', 'What to observe and record on the road', 'Common first-trip issues and quick fixes', 'An adjustment protocol before a longer journey']
}, {
  icon: SettingsIcon,
  number: 16,
  title: 'Maintenance',
  subtitle: 'Protect your investment for years',
  description: 'Keep your systems ready for the next adventure.',
  items: ['Annual routines for core systems', 'Recommended preventive inspections', 'Preparing before long trips', 'Essential tools and spare parts', 'A maintenance log that keeps nothing overlooked']
}];
const BONUSES: Bonus[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/5x8jrpugNvAGafQBvwpAKe/bono_1_camper_ingles.png',
  title: 'Master Materials List',
  description: 'A phase-by-phase materials list to help you estimate, compare options, and plan before you begin.',
  benefit: 'Make smarter purchasing decisions from day one.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/4mxGp3yA913TJnTpXXYuB8/camper_bono_2_ingles.png',
  title: 'Complete Electrical Reference Tables',
  description: 'Technical reference tables for cable sizing, fuses, battery states, typical consumption, and useful formulas.',
  benefit: 'Keep clear technical data within reach when you need it most.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/m3oUPQz443Kjgot2MZ2nCp/camper_bono_3_ingles.png',
  title: 'Step-by-Step Solar Calculator',
  description: 'Worked examples and a worksheet to calculate panels, battery bank, and controller for your own setup.',
  benefit: 'Size your solar system before you spend on components.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/3xdYssHDnU7XeMyptHUV5a/4b603640-bd2e-4360-810d-a59dc92ff05f.png',
  title: 'Printable Build Checklists',
  description: 'Phase-by-phase checklists to take into the workshop, track progress, and avoid missing important steps.',
  benefit: 'Build with confidence and reduce avoidable rework.'
}];
function initializeMetaPixel() {
  const metaWindow = window as MetaWindow;
  if (!metaWindow.fbq) {
    const fbq = ((event: string, ...parameters: unknown[]) => {
      if (fbq.callMethod) {
        fbq.callMethod(event, ...parameters);
        return;
      }
      fbq.queue?.push([event, ...parameters]);
    }) as MetaPixel;
    fbq.push = (...parameters: unknown[]) => fbq(...parameters);
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    metaWindow.fbq = fbq;
    metaWindow._fbq = fbq;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }
  const pixel = metaWindow.fbq;
  if (!pixel) return;
  if (!metaWindow.initializedMetaPixels?.includes(META_PIXEL_ID)) {
    pixel('init', META_PIXEL_ID);
    metaWindow.initializedMetaPixels = [...(metaWindow.initializedMetaPixels ?? []), META_PIXEL_ID];
  }
  if (!metaWindow.metaPageViewSent) {
    pixel('track', 'PageView');
    metaWindow.metaPageViewSent = true;
  }
}
export function SalesPage() {
  const [timeLeft, setTimeLeft] = useState({
    minutes: 14,
    seconds: 59
  });
  useEffect(() => {
    initializeMetaPixel();
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(previous => {
        if (previous.seconds > 0) return {
          ...previous,
          seconds: previous.seconds - 1
        };
        if (previous.minutes > 0) return {
          minutes: previous.minutes - 1,
          seconds: 59
        };
        return previous;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const scrollToPurchaseSection = () => {
    document.getElementById('purchase-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const trackInitiateCheckout = () => {
    const metaWindow = window as MetaWindow;
    metaWindow.fbq?.('track', 'InitiateCheckout', {
      content_name: 'Complete Van Conversion Master Guide',
      currency: 'USD',
      value: 27
    });
  };
  const formatTime = (number: number) => number.toString().padStart(2, '0');
  return <div className="min-h-screen w-full bg-stone-50 font-sans selection:bg-amber-200 selection:text-stone-900" data-id="element-20">
      <PurchasePopup data-id="element-21" />

      <div className="bg-stone-900 px-4 py-2 text-center text-xs font-medium leading-tight text-stone-200 md:py-3 md:text-sm" data-id="element-22">
        FOR ANYONE DREAMING OF BUILDING A HOME ON WHEELS—BUT NOT SURE WHERE TO
        START
      </div>

      <header className="mx-auto max-w-4xl px-4 pb-5 pt-5 text-center md:pb-10 md:pt-10" data-id="element-23">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-amber-600" data-id="element-24">
          The Complete Van Conversion Master Guide
        </p>
        <h1 className="mb-5 text-3xl font-extrabold leading-tight text-stone-900 md:text-6xl" data-id="element-25">
          Turn your vehicle into a home on wheels with a clear, complete,
          step-by-step system.
        </h1>

        <button type="button" onClick={scrollToPurchaseSection} className="group mx-auto mb-5 block w-full max-w-2xl rounded-2xl focus:outline-none focus:ring-4 focus:ring-amber-300" aria-label="View the Complete Van Conversion Master Guide offer" data-id="element-26">
          <img src={PRODUCT_IMAGE_URL} alt="The Complete Van Conversion guide and system" className="mx-auto h-auto w-full rounded-2xl transition-transform duration-300 group-hover:scale-[1.015]" loading="eager" data-id="element-27" />
        </button>

        <p className="mb-5 text-xs font-medium uppercase tracking-wide text-stone-500 md:text-sm" data-id="element-28">
          16 modules + exclusive resources + 4 bonuses · Instant download
        </p>

        <ul className="mx-auto mb-6 max-w-xl space-y-2 text-left text-sm text-stone-700 md:text-lg" data-id="element-29">
          {['560+ pages—from an empty vehicle to your first trip', 'Electrical, water, gas, solar, furniture, and safety systems', 'An organized method for making the right decisions in the right order', 'Practical guidance for vans, minibuses, and larger vehicles'].map(feature => <li key={feature} className="flex items-start gap-2" data-id="element-30">
              <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" aria-hidden="true" data-id="element-31" />
              <span data-id="element-32">{feature}</span>
            </li>)}
        </ul>

        <button type="button" onClick={scrollToPurchaseSection} className="mx-auto flex w-full max-w-lg items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-base font-extrabold text-white shadow-[0_5px_0_rgb(5,150,105)] transition-all hover:translate-y-1 hover:bg-emerald-400 hover:shadow-[0_2px_0_rgb(5,150,105)] focus:outline-none focus:ring-4 focus:ring-emerald-200 md:text-xl" data-id="element-33">
          GET THE MASTER GUIDE
          <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-34" />
        </button>
        <p className="mt-3 text-xs font-medium text-stone-500 md:text-sm" data-id="element-35">
          One-time payment of USD 27 · Instant access
        </p>
      </header>

      <main data-id="element-36">
        <section className="bg-stone-900 px-4 py-20 text-stone-300" data-id="element-37">
          <div className="mx-auto max-w-3xl space-y-6 text-lg leading-relaxed" data-id="element-38">
            <h2 className="mb-8 text-3xl font-bold text-white md:text-4xl" data-id="element-39">
              The problem is not a lack of time or motivation.
            </h2>
            <p data-id="element-40">
              When you decide to convert a vehicle, the information feels
              endless: videos, forums, opinions, and solutions that do not
              always apply to your vehicle or travel style.
            </p>
            <p className="my-8 border-l-4 border-amber-500 py-2 pl-6 text-xl font-semibold italic text-amber-400" data-id="element-41">
              Some people turn an idea into a road-ready camper. Others keep
              collecting open tabs.
            </p>
            <p data-id="element-42">
              The block usually appears when real decisions must be made: cable
              sizes, battery type, system order, or the interior layout.
            </p>
            <p data-id="element-43">
              Or you buy materials, install something, and later discover it all
              has to come out. The cost is not only financial—you lose time,
              confidence, and momentum.
            </p>
            <p className="pt-2 text-xl font-bold text-white" data-id="element-44">
              The frustrating part is not moving slowly. It is not knowing what
              the next step should be.
            </p>
          </div>
        </section>

        <section className="bg-white px-4 py-20" data-id="element-45">
          <div className="mx-auto max-w-4xl" data-id="element-46">
            <h2 className="mb-12 text-center text-3xl font-bold text-stone-900" data-id="element-47">
              Why so many conversion projects stall
            </h2>
            <div className="mb-16 grid gap-6 md:grid-cols-2" data-id="element-48">
              {['People learn from fragmented content that does not follow a build sequence.', 'Advice conflicts, and there is no clear framework for making decisions.', 'Electrical and gas systems feel intimidating when explained with too much jargon.', 'Planning gets left until the end, when corrections cost more time and money.'].map(problem => <article key={problem} className="rounded-2xl border border-stone-200 bg-stone-50 p-6" data-id="element-49">
                  <AlertTriangleIcon className="mb-4 h-8 w-8 text-amber-500" aria-hidden="true" data-id="element-50" />
                  <p className="text-stone-700" data-id="element-51">{problem}</p>
                </article>)}
            </div>
            <div className="rounded-2xl bg-stone-100 p-8 text-center" data-id="element-52">
              <p className="mb-4 text-lg text-stone-700" data-id="element-53">
                People who finish their builds were not born knowing every
                answer. They followed a sequence, checked each stage, and built
                from a plan.
              </p>
              <p className="text-2xl font-bold text-stone-900" data-id="element-54">
                That system is your starting point.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-stone-900 px-4 py-20 text-stone-300" data-id="element-55">
          <div className="mx-auto max-w-4xl" data-id="element-56">
            <h2 className="mb-6 text-center text-3xl font-bold text-white md:text-4xl" data-id="element-57">
              The real conversion roadblock
            </h2>
            <p className="mb-12 text-center text-xl" data-id="element-58">
              Most projects stop for three concrete reasons:
            </p>
            <div className="space-y-6" data-id="element-59">
              {[['There is no organized system', 'Scattered information does not tell you what to do first or how one decision affects the next.'], ['There is a fear of expensive mistakes', 'A poorly planned installation can mean removing finished work, buying materials twice, and delaying your project.'], ['There is no clear guide for each phase', 'Videos can inspire you, but they do not replace a complete path to plan, build, and check your camper.']].map(([title, description], index) => <article key={title} className="rounded-2xl border border-stone-700 bg-stone-800 p-8" data-id="element-60">
                  <h3 className="mb-4 text-xl font-bold text-amber-400" data-id="element-61">
                    Reason {index + 1}: {title}
                  </h3>
                  <p data-id="element-62">{description}</p>
                </article>)}
            </div>
            <div className="mt-12 text-center" data-id="element-63">
              <p className="mb-10 text-2xl font-bold text-white" data-id="element-64">
                It is not a lack of ability. It is a lack of a reliable roadmap.
              </p>
              <button type="button" onClick={scrollToPurchaseSection} className="inline-flex items-center justify-center gap-3 rounded-xl bg-emerald-500 px-8 py-4 text-lg font-extrabold text-white shadow-[0_6px_0_rgb(5,150,105)] transition-all hover:translate-y-1 hover:bg-emerald-400 hover:shadow-[0_3px_0_rgb(5,150,105)] focus:outline-none focus:ring-4 focus:ring-emerald-200" data-id="element-65">
                SEE THE COMPLETE SYSTEM
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-66" />
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-24 text-center" data-id="element-67">
          <div className="mx-auto max-w-4xl" data-id="element-68">
            <p className="mb-4 text-sm font-bold uppercase tracking-widest text-amber-600" data-id="element-69">
              The solution
            </p>
            <h2 className="mb-8 text-4xl font-extrabold text-stone-900 md:text-5xl" data-id="element-70">
              THE COMPLETE VAN CONVERSION MASTER GUIDE
            </h2>
            <p className="mb-8 text-2xl font-medium text-stone-700" data-id="element-71">
              The complete system for building your home on wheels from scratch.
            </p>
            <p className="mx-auto mb-12 max-w-3xl text-xl text-stone-600" data-id="element-72">
              No guessing. No piecing together scattered advice. No wasting
              resources on mistakes you can avoid.
            </p>
            <div className="rounded-3xl border border-stone-200 bg-stone-50 p-8 text-left md:p-12" data-id="element-73">
              <p className="mb-6 text-lg leading-relaxed text-stone-700" data-id="element-74">
                The entire system is organized to take you from the first
                decision to your first trip. Every stage connects to the next
                one, reducing mistakes and simplifying the build.
              </p>
              <p className="mb-8 text-lg leading-relaxed text-stone-700" data-id="element-75">
                The <strong data-id="element-76">Sequential Build Method</strong> turns technical
                choices into clear actions: plan, prepare, install, test, and
                enjoy.
              </p>
              <blockquote className="border-l-4 border-amber-500 py-2 pl-6 text-xl font-medium italic text-stone-800" data-id="element-77">
                “A van conversion is not built with improvised decisions. It is
                built with the right decisions in the right order.”
              </blockquote>
            </div>
          </div>
        </section>

        <section className="bg-stone-100 px-4 py-20" data-id="element-78">
          <div className="mx-auto max-w-4xl" data-id="element-79">
            <h2 className="mb-12 text-center text-4xl font-bold text-stone-900" data-id="element-80">
              WHAT YOU GET TODAY
            </h2>
            <article className="mb-16 rounded-3xl border border-stone-200 bg-white p-8 shadow-sm md:p-12" data-id="element-81">
              <h3 className="mb-6 text-2xl font-bold text-stone-900" data-id="element-82">
                The Sequential Build Method
              </h3>
              <p className="mb-6 text-lg text-stone-700" data-id="element-83">
                The most common mistake is trying to build from intuition or
                disconnected videos. This guide gives you a sequence that lets
                every decision support the next.
              </p>
              <p className="mb-6 text-lg text-stone-700" data-id="element-84">
                From interior design to core systems, each step is positioned to
                help prevent you from tearing out finished work later.
              </p>
              <p className="text-xl font-bold text-stone-900" data-id="element-85">
                One system. Every phase. A clear roadmap forward.
              </p>
            </article>
            <h3 className="mb-4 text-center text-3xl font-bold text-stone-900" data-id="element-86">
              The step-by-step system
            </h3>
            <p className="mb-12 text-center text-xl text-stone-600" data-id="element-87">
              16 modules designed to take you from an empty vehicle to your
              first trip.
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-id="element-88">
              {GUIDE_MODULES.map(module => {
              const Icon = module.icon;
              return <article key={module.number} className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm md:p-8" data-id="element-89">
                    <div className="mb-4 flex items-center gap-3" data-id="element-90">
                      <Icon className="h-6 w-6 flex-shrink-0 text-amber-500" aria-hidden="true" data-id="element-91" />
                      <h4 className="text-xl font-bold text-stone-900" data-id="element-92">
                        Module {module.number}: {module.title}
                      </h4>
                    </div>
                    <p className="mb-4 font-medium text-stone-900" data-id="element-93">
                      {module.subtitle}
                    </p>
                    <p className="mb-4 italic text-stone-600" data-id="element-94">
                      {module.description}
                    </p>
                    <ul className="ml-4 list-disc space-y-2 text-stone-700 marker:text-amber-500" data-id="element-95">
                      {module.items.map(item => <li key={item} data-id="element-96">{item}</li>)}
                    </ul>
                  </article>;
            })}
            </div>
          </div>
        </section>

        <section className="bg-stone-900 px-4 py-24 text-stone-200" data-id="element-97">
          <div className="mx-auto max-w-4xl" data-id="element-98">
            <div className="mb-16 text-center" data-id="element-99">
              <GiftIcon className="mx-auto mb-6 h-16 w-16 text-amber-500" aria-hidden="true" data-id="element-100" />
              <h2 className="mb-4 text-4xl font-bold text-white" data-id="element-101">
                4 BONUSES INCLUDED
              </h2>
              <p className="text-xl text-stone-400" data-id="element-102">
                Practical resources to help you move faster with more clarity.
              </p>
            </div>
            <div className="space-y-8" data-id="element-103">
              {BONUSES.map((bonus, index) => <article key={bonus.title} className="rounded-3xl border border-stone-700 bg-stone-800 p-6 md:p-8" data-id="element-104">
                  <div className="flex flex-col items-center gap-8 md:flex-row" data-id="element-105">
                    <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl bg-stone-700 md:w-1/3" data-id="element-106">
                      <img src={bonus.image} alt={`Bonus ${index + 1}: ${bonus.title}`} className="h-full w-full object-contain" loading="lazy" data-id="element-107" />
                    </div>
                    <div className="w-full md:w-2/3" data-id="element-108">
                      <h3 className="mb-2 text-2xl font-bold text-white" data-id="element-109">
                        Bonus {index + 1}: {bonus.title}
                      </h3>
                      <p className="mb-4 text-stone-400" data-id="element-110">{bonus.description}</p>
                      <p className="mb-4 font-medium text-amber-400" data-id="element-111">
                        {bonus.benefit}
                      </p>
                      <p className="text-xl font-bold text-white" data-id="element-112">
                        INCLUDED AT NO EXTRA COST
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        <section className="bg-amber-500 px-4 py-20 text-stone-900" data-id="element-113">
          <div className="mx-auto max-w-3xl" data-id="element-114">
            <h2 className="mb-10 text-center text-3xl font-extrabold md:text-4xl" data-id="element-115">
              THIS GUIDE IS FOR YOU IF…
            </h2>
            <ul className="space-y-4 text-lg font-medium md:text-xl" data-id="element-116">
              {['You have a vehicle and need a clear path to get started.', 'You want to build it yourself and make better decisions.', 'You want electrical, water, and gas systems explained without confusion.', 'You want a functional, organized, long-lasting finish.', 'You want to plan safety and travel requirements in advance.', 'You plan to travel or spend extended time in your home on wheels.', 'You want to handle common road issues without improvising.'].map(item => <li key={item} className="flex items-start gap-4 rounded-xl bg-amber-400/30 p-4" data-id="element-117">
                  <CheckCircle2Icon className="mt-0.5 h-7 w-7 flex-shrink-0" aria-hidden="true" data-id="element-118" />
                  <span data-id="element-119">{item}</span>
                </li>)}
            </ul>
          </div>
        </section>

        <section className="bg-stone-100 px-4 py-24" data-id="element-120">
          <div className="mx-auto max-w-6xl" data-id="element-121">
            <h2 className="mb-16 text-center text-3xl font-bold text-stone-900 md:text-4xl" data-id="element-122">
              WHAT BUILDERS ARE SAYING
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-123">
              <TestimonialCard quote="I had watched countless videos without knowing how to organize the project. The guide helped me plan the prep work and insulation in my first weekend." name="James M." role="DIY builder" age="34" location="Digital access" data-id="element-124" />
              <TestimonialCard quote="The electrical system worried me most. This guide explains it clearly, and now we have a safe plan to install it." name="Daniel R." role="Traveling couple" age="41" location="Digital access" data-id="element-125" />
              <TestimonialCard quote="The best part is that I did not have to look for every answer separately. Everything is in a logical order and works across different vehicles." name="Laura M." role="Remote worker" age="38" location="Digital access" data-id="element-126" />
              <TestimonialCard quote="The planning chapter saved me from expensive mistakes before I bought materials. It paid for itself right there." name="Frederick S." role="DIY builder" age="52" location="Digital access" data-id="element-127" />
              <div className="md:col-span-2 lg:col-span-1" data-id="element-128">
                <TestimonialCard quote="We needed a practical, safe setup for family trips. The guide gave us clarity for every important decision." name="Sebastian V." role="Traveling family" age="45" location="Digital access" data-id="element-129" />
              </div>
            </div>
          </div>
        </section>

        <section id="purchase-section" className="scroll-mt-10 bg-white px-4 py-24" data-id="element-130">
          <div className="mx-auto max-w-4xl" data-id="element-131">
            <div className="relative overflow-hidden rounded-3xl bg-stone-900 p-5 text-center md:p-12" data-id="element-132">
              <div className="absolute left-0 top-0 flex w-full items-center justify-center gap-2 bg-amber-500 py-2 text-sm font-bold text-stone-900 md:text-base" data-id="element-133">
                <ClockIcon className="h-5 w-5" aria-hidden="true" data-id="element-134" />
                OFFER AVAILABLE FOR: {formatTime(timeLeft.minutes)}:
                {formatTime(timeLeft.seconds)}
              </div>

              <div className="mb-8 mt-10" data-id="element-135">
                <h2 className="mb-3 text-3xl font-bold text-white md:text-4xl" data-id="element-136">
                  Get the complete Master Guide
                </h2>
                <p className="text-base text-stone-300 md:text-lg" data-id="element-137">
                  One complete offer with every resource you need to plan and
                  build with confidence.
                </p>
              </div>

              <article className="mx-auto mb-8 max-w-3xl rounded-2xl border-2 border-cyan-400 bg-stone-800 p-6 text-left md:p-8" data-id="element-138">
                <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-start" data-id="element-139">
                  <div data-id="element-140">
                    <div className="mb-2 flex items-center gap-3" data-id="element-141">
                      <BookOpenIcon className="h-7 w-7 text-cyan-400" aria-hidden="true" data-id="element-142" />
                      <h3 className="text-2xl font-bold text-white" data-id="element-143">
                        MASTER GUIDE
                      </h3>
                    </div>
                    <p className="text-stone-300" data-id="element-144">
                      The complete system to turn your vehicle into a home on
                      wheels, with instant digital access.
                    </p>
                  </div>
                  <p className="whitespace-nowrap text-3xl font-extrabold text-cyan-400" data-id="element-145">
                    USD 27
                  </p>
                </div>
                <div className="space-y-3 rounded-xl bg-stone-900 p-5 text-sm md:text-base" data-id="element-146">
                  {['Master Guide with 16 step-by-step modules', '4 practical bonuses: materials list, electrical tables, solar calculator, and printable checklists', 'Master Plans with measurements and build criteria for greater precision', 'A wire-by-wire solar guide with diagrams and MPPT configuration', 'A Roadside Survival Manual with solutions for common setbacks'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-147">
                      <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-400" aria-hidden="true" data-id="element-148" />
                      <p className="text-stone-200" data-id="element-149">{item}</p>
                    </div>)}
                </div>
              </article>

              <p className="mx-auto mb-5 max-w-lg text-sm text-stone-300 md:text-base" data-id="element-150">
                Continue to secure checkout to complete your purchase. Your
                access is activated automatically after payment.
              </p>
              <a href={CHECKOUT_URL} onClick={trackInitiateCheckout} className="mx-auto flex w-full max-w-lg items-center justify-center gap-2 rounded-xl bg-cyan-400 px-8 py-5 text-xl font-extrabold text-stone-900 shadow-[0_8px_0_rgb(6,182,212)] transition-all hover:translate-y-1 hover:bg-cyan-300 hover:shadow-[0_4px_0_rgb(6,182,212)] focus:outline-none focus:ring-4 focus:ring-cyan-200" data-id="element-151">
                GET THE MASTER GUIDE FOR USD 27
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-152" />
              </a>
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-stone-400" data-id="element-153">
                <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-154" />
                Secure, encrypted payment
              </div>
            </div>
          </div>
        </section>

        <section className="bg-stone-50 px-4 py-20" data-id="element-155">
          <div className="mx-auto max-w-3xl" data-id="element-156">
            <h2 className="mb-12 text-center text-3xl font-bold text-stone-900" data-id="element-157">
              FREQUENTLY ASKED QUESTIONS
            </h2>
            <div className="space-y-6" data-id="element-158">
              {[['What format is the guide in?', 'You receive an instantly downloadable PDF. Read it on your phone, tablet, or computer, and print it if you wish.'], ['Do I need previous technical knowledge?', 'No. The guide is designed for beginners and explains every topic from the ground up with practical examples.'], ['What type of vehicle is it for?', 'It works for compact vans, cargo vans, minibuses, and larger vehicles. It includes planning principles for different configurations.'], ['Can I use it if I have already started my project?', 'Yes. The modules are independent, so you can start from the phase that matches your current progress.'], ['Are regulations the same in every country?', 'No. The guide shares safety and planning principles; always check with local professionals and authorities for requirements where you travel.']].map(([question, answer]) => <article key={question} className="rounded-2xl border border-stone-200 bg-white p-6" data-id="element-159">
                  <h3 className="mb-2 text-lg font-bold text-stone-900" data-id="element-160">
                    {question}
                  </h3>
                  <p className="text-stone-700" data-id="element-161">{answer}</p>
                </article>)}
            </div>
          </div>
        </section>

        <section className="bg-emerald-900 px-4 py-20 text-emerald-50" data-id="element-162">
          <div className="mx-auto max-w-3xl text-center" data-id="element-163">
            <ShieldCheckIcon className="mx-auto mb-6 h-20 w-20 text-emerald-400" aria-hidden="true" data-id="element-164" />
            <h2 className="mb-8 text-3xl font-bold text-white md:text-4xl" data-id="element-165">
              7-DAY FULL GUARANTEE
            </h2>
            <p className="mb-6 text-xl" data-id="element-166">Use the guide for a full week.</p>
            <p className="mb-8 text-lg" data-id="element-167">If you do not gain:</p>
            <ul className="mx-auto mb-10 max-w-xl space-y-4 text-left text-emerald-100" data-id="element-168">
              {['More clarity on what to do in each phase', 'More confidence with electrical, gas, and safety decisions', 'A concrete plan to move forward without guessing'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-169">
                  <CheckCircle2Icon className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-400" aria-hidden="true" data-id="element-170" />
                  <span data-id="element-171">{item}</span>
                </li>)}
            </ul>
            <p className="mb-4 text-2xl font-bold text-white" data-id="element-172">
              We will refund 100% of your money.
            </p>
            <p className="text-emerald-300" data-id="element-173">
              No questions. No forms. No complications.
            </p>
          </div>
        </section>
      </main>

      <footer className="bg-stone-950 px-4 py-12 text-center text-sm text-stone-400" data-id="element-174">
        <div className="mx-auto max-w-4xl space-y-6" data-id="element-175">
          <div className="flex flex-wrap items-center justify-center gap-2" data-id="element-176">
            <DownloadIcon className="h-5 w-5 text-stone-500" aria-hidden="true" data-id="element-177" />
            <span className="font-medium text-stone-300" data-id="element-178">DIGITAL PRODUCT</span>
            <span aria-hidden="true" data-id="element-179">·</span>
            <span data-id="element-180">This product is delivered automatically after purchase.</span>
          </div>
          <p data-id="element-181">Instant access. PDF download. Lifetime access.</p>
          <div className="border-t border-stone-800 pt-8" data-id="element-182">
            <p className="mb-4" data-id="element-183">
              © 2026 Complete Van Conversion Master Guide. All rights reserved.
            </p>
            <p className="mx-auto max-w-2xl text-xs leading-relaxed text-stone-600" data-id="element-184">
              This site is not part of Facebook and is not endorsed by Facebook.
              Facebook is a trademark of Meta Platforms, Inc.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}